import type { AvailablePaymentMethod, PaymentMethod } from '@/types/checkout';
import { PaymentMethodEnum } from '@/types/common';

/**
 * The method a payment is ATTRIBUTED to (logs, SDK events, retry state): an
 * express wallet when it started one, else the real APM behind the "Other"
 * tile rather than `other` itself.
 */
export const resolveAttributedPaymentMethod = (
  paymentMethodSelected: PaymentMethodEnum,
  otherMethodSelected: PaymentMethodEnum | undefined,
  payFromExpressCheckout?: PaymentMethodEnum
): PaymentMethodEnum => {
  if (payFromExpressCheckout) return payFromExpressCheckout;
  if (paymentMethodSelected === PaymentMethodEnum.OTHER) return otherMethodSelected ?? paymentMethodSelected;
  return paymentMethodSelected;
};

/**
 * Standard "pick the first non-express method, fall back to the very first"
 * default-selection rule. Used by DEFAULT and SINGLE_PAYMENT_METHOD.
 */
export const pickFirstNonExpressPaymentMethod = (
  paymentMethods: AvailablePaymentMethod[]
): PaymentMethodEnum | undefined => {
  const firstNonExpress = paymentMethods.find((pm) => !pm.isExpress);
  const chosen = firstNonExpress ?? paymentMethods[0];
  return chosen?.paymentMethodName as PaymentMethodEnum | undefined;
};

/**
 * Is `method` present in the BE-returned payment methods list?
 *
 * Method-agnostic on purpose — `PREFERRED_PAYMENT_METHOD` uses this for
 * both the FTD branch (device's express wallet) and the last-method
 * branch (paypal/link/card/…). Don't reintroduce an "express" name
 * here; the method type lives in the argument, not the helper.
 */
export const isPaymentMethodAvailable = (
  method: PaymentMethodEnum | undefined,
  paymentMethods: PaymentMethod[]
): boolean => {
  if (!method) return false;
  return paymentMethods.some((pm) => pm.paymentMethodName === method);
};

/**
 * Returns the human-readable display name for a payment method enum value.
 * Falls back to the raw enum string when no matching settings entry exists.
 */
export const getPaymentMethodDisplayName = (
  method: string | undefined,
  paymentMethods: PaymentMethod[]
): string | undefined => {
  if (!method) return undefined;
  const matched = paymentMethods.find((pm) => pm.paymentMethodName === method);
  return matched?.paymentMethodDisplayName ?? method;
};
